import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ProducerTermsStatus {
  producerId: string;
  requiredDocumentId: string | null;
  requiredVersion: string;
  requiredTitle: string | null;
  acceptedDocumentId: string | null;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  acceptedBy: string | null;
  isAccepted: boolean;
}

export interface CurrentTermsDocument {
  id: string;
  version: string;
  title: string;
  content_md: string;
  effective_from: string | null;
  published_at: string | null;
}

/**
 * Hook for fetching producer terms status and accepting terms.
 * Now uses legal_documents registry for versioned terms.
 */
export function useProducerTerms(producerId: string | undefined) {
  const queryClient = useQueryClient();

  const {
    data: termsStatus,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['producer-terms-status', producerId],
    queryFn: async (): Promise<ProducerTermsStatus | null> => {
      if (!producerId || producerId === 'demo') return null;

      const { data, error } = await supabase
        .from('producer_terms_status')
        .select('*')
        .eq('producer_id', producerId)
        .maybeSingle();

      if (error) {
        console.error('[useProducerTerms] Error fetching terms status:', error);
        throw error;
      }

      if (!data) return null;

      return {
        producerId: data.producer_id,
        requiredDocumentId: data.required_document_id,
        requiredVersion: data.required_version ?? '1.0',
        requiredTitle: data.required_title,
        acceptedDocumentId: data.accepted_document_id,
        acceptedVersion: data.accepted_version,
        acceptedAt: data.accepted_at,
        acceptedBy: data.accepted_by,
        isAccepted: data.is_accepted ?? false,
      };
    },
    enabled: !!producerId && producerId !== 'demo',
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const acceptTermsMutation = useMutation({
    mutationFn: async ({ termsVersion }: { termsVersion: string }) => {
      if (!producerId) throw new Error('Producer ID required');

      const { data, error } = await supabase.rpc('accept_producer_terms', {
        p_producer_id: producerId,
        p_terms_version: termsVersion,
      });

      if (error) {
        console.error('[useProducerTerms] Error accepting terms:', error);
        throw error;
      }

      // Check for RPC-level errors
      if (data && typeof data === 'object' && 'success' in data && !data.success) {
        throw new Error((data as { error?: string }).error || 'Failed to accept terms');
      }

      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch terms status
      queryClient.invalidateQueries({ queryKey: ['producer-terms-status', producerId] });
    },
  });

  return {
    termsStatus,
    isLoading,
    error,
    refetch,
    // Convenience accessors
    requiredVersion: termsStatus?.requiredVersion ?? '1.0',
    requiredTitle: termsStatus?.requiredTitle ?? 'Värdvillkor för Farmly',
    acceptedVersion: termsStatus?.acceptedVersion ?? null,
    isAccepted: termsStatus?.isAccepted ?? false,
    // Mutation
    acceptTerms: (termsVersion: string) => acceptTermsMutation.mutateAsync({ termsVersion }),
    isAccepting: acceptTermsMutation.isPending,
    acceptError: acceptTermsMutation.error,
  };
}

/**
 * Hook for fetching the current published producer terms document.
 * Used by the public legal page to render terms from DB.
 */
export function useCurrentProducerTerms() {
  return useQuery({
    queryKey: ['current-producer-terms'],
    queryFn: async (): Promise<CurrentTermsDocument | null> => {
      const { data, error } = await supabase
        .rpc('get_current_producer_terms')
        .maybeSingle();

      if (error) {
        console.error('[useCurrentProducerTerms] Error fetching terms:', error);
        throw error;
      }

      if (!data) return null;

      return {
        id: data.id,
        version: data.version,
        title: data.title,
        content_md: data.content_md,
        effective_from: data.effective_from,
        published_at: data.published_at,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - legal docs don't change often
  });
}
